/* Routine section ("Ekspertens rutine") (sections/productpage-routine.liquid): scent/size chips per step, the total, and one add for the ticked steps. */
if (!customElements.get("routine-builder")) {
  class RoutineBuilder extends HTMLElement {
    connectedCallback() {
      this.steps = [...this.querySelectorAll("[data-routine-step]")].map((el) => ({
        el,
        variants: JSON.parse(el.querySelector("[data-routine-variants]").textContent),
        toggle: el.querySelector("[data-routine-toggle]"),
      }));
      this.totalEl = this.querySelector("[data-routine-total]");
      this.addBtn = this.querySelector("[data-routine-add]");
      this.addLabel = this.querySelector("[data-routine-add-label]");

      this.steps.forEach((step) => this.bindStep(step));
      this.addBtn.addEventListener("click", () => this.add());

      // A new scent or size in the buy box re-renders the routine for that variant (scent preference, "your pick").
      this.onVariantChange = (event) => {
        const variant = event.target.current_variant;
        if (variant && variant.id && String(variant.id) !== this.dataset.variantId) this.reload(variant.id);
      };
      document.addEventListener("variant:changed", this.onVariantChange);

      // Phone: each step's advice folds to its first line; "Læs mere" unfolds it.
      this.querySelectorAll("[data-routine-more]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const advice = btn.closest(".routine-step__advice");
          const open = !advice.classList.contains("is-open");
          advice.classList.toggle("is-open", open);
          btn.setAttribute("aria-expanded", open ? "true" : "false");
          btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
        });
      });
      this.onResize = () => this.markFolds();
      window.addEventListener("resize", this.onResize);

      // While this routine's add is on screen the product page's pinned add steps aside: two buttons, one job each.
      if (window.IntersectionObserver && document.querySelector(".pdp__sticky-atc")) {
        this.addObserver = new IntersectionObserver(([entry]) => document.body.classList.toggle("routine-add-in-view", entry.isIntersecting));
        this.addObserver.observe(this.addBtn);
      }

      this.buildExtraChips();
      this.layout();
      if (this.dataset.layout === "summary") this.initStepper();
      this.update();
      this.markFolds();
    }

    // --- Layout "summary": the steps as short rows; "Tilpas rutinen" shows one step per screen (like Bedre Nætter's
    // "Design og køb"). The cards, chips, extras and the add are the same as in the card layout, only shown one at a time.
    initStepper() {
      this.summaryList = this.querySelector("[data-routine-summary-list]");
      this.stepperCount = this.querySelector("[data-routine-stepper-count]");
      this.stepperBar = this.querySelector("[data-routine-stepper-bar]");
      this.prevBtn = this.querySelector("[data-routine-prev]");
      this.nextBtn = this.querySelector("[data-routine-next]");
      this.querySelector("[data-routine-open-steps]").addEventListener("click", () => this.openSteps(0));
      this.querySelector("[data-routine-stepper-close]").addEventListener("click", () => this.closeSteps());
      // "Se rutine": back to the overview of the routine (the summary rows).
      // "Se rutine" lands on the rows, not wherever the page was left (owner, 2026-10-19: it showed the footer).
      this.querySelector("[data-routine-see]").addEventListener("click", () => {
        this.closeSteps();
        const rows = this.summaryList && (this.summaryList.closest(".routine__summary") || this.summaryList);
        if (rows) {
          // Under the page's pinned header; in the guide's panel there is none.
          const header = this.closest("dra-wer, dialog") ? 0 : parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--nav-height")) || 0;
          rows.style.scrollMarginTop = header + 16 + "px";
          rows.scrollIntoView({ block: "start" });
        }
      });
      this.prevBtn.addEventListener("click", () => this.showStep(this.stepIndex - 1));
      this.nextBtn.addEventListener("click", () => {
        if (this.stepIndex >= this.stepperList().length - 1) this.closeSteps();
        else this.showStep(this.stepIndex + 1);
      });
      // While a step is open, Tab stays inside the step panel; Esc closes it.
      this.addEventListener("keydown", (event) => {
        if (!this.classList.contains("is-stepping")) return;
        if (event.key === "Escape") return this.closeSteps();
        if (event.key !== "Tab") return;
        const col = this.querySelector("[data-routine-steps-col]");
        const focusable = [...col.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])")].filter(
          (el) => el.offsetParent !== null
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    // Every step in order, extras included (an extra not yet added offers itself on its screen).
    sequence() {
      return this.steps.filter((s) => !s.el.hidden);
    }

    // What the dialog's arrows go through. From a result row only the routine's own products: an extra not added stays
    // in the overview (owner, 2026-10-19: paging on to a product that isn't in the routine confused), unless it is the
    // row that was opened, which then shows alone.
    stepperList() {
      const list = this.sequence();
      if (!this.classList.contains("is-details")) return list;
      if (this.detailsExtra) return [this.detailsExtra];
      return list.filter((s) => !s.el.classList.contains("is-collapsed"));
    }

    renderSummary() {
      if (!this.summaryList) return;
      const addLabel = this.summaryList.dataset.labelAdd;
      // The routine first; the extras not yet added below, under the PDP's own heading (owner, 2026-10-19: one mixed
      // list read as if every row were part of the routine).
      // Rows in the routine are numbered in the order of use ("Trin 1 · Rens"), from the steps list's own wording.
      const stepWord = (this.querySelector(".routine__steps") || {}).dataset?.stepTemplate || "";
      // The "Morgen og aften" tag as the routine writes it (icons and words), for a day cream that also goes on at night.
      if (this.bothWhen === undefined) this.bothWhen = ([...this.querySelectorAll(".routine-step__when")].find((w) => /morgen.*aften/i.test(w.textContent)) || {}).innerHTML || null;
      let order = 0;
      const rows = this.sequence().map((step, index) => {
          const el = step.el;
          const extra = el.classList.contains("is-collapsed");
          const off = !extra && !step.toggle.checked;
          const li = document.createElement("li");
          // Find din rutine: the row is a card with two actions (open the product, "Skift"), so a div with the name as the
          // stretched open button, as the builder's overview rows; elsewhere the whole row is one button.
          const guide = !extra && !!this.closest("[data-guide-routine]");
          const row = document.createElement(guide ? "div" : "button");
          if (!guide) row.type = "button";
          row.className = "routine__row" + (extra ? " routine__row--extra" : "") + (off ? " routine__row--off" : "");
          const img = el.querySelector(".routine-step__img img, .routine-step__tab-img img");
          const thumb = document.createElement("span");
          thumb.className = "routine__row-img";
          if (img) {
            const copy = document.createElement("img");
            copy.src = img.currentSrc || img.src;
            copy.alt = "";
            thumb.append(copy);
          }
          const text = document.createElement("span");
          text.className = "routine__row-text";
          const label = document.createElement("span");
          label.className = "routine__row-label";
          const stepLabel = ((el.querySelector(".routine-step__label") || {}).textContent || "").trim();
          if (!extra && !off && stepWord) order += 1;
          label.textContent = !extra && !off && stepWord ? stepWord.replace("[n]", order) + " · " + stepLabel : stepLabel;
          const name = document.createElement("span");
          name.className = "routine__row-name";
          name.textContent = (el.querySelector(".routine-step__name") || {}).textContent || "";
          // Scent and size on a line of their own under the name, whole (owner, 2026-10-20: appended to the clamped
          // name they were cut off, a "·" left hanging); a product without options shows its size.
          const variant = el.querySelector("[data-routine-summary]") || el.querySelector(".routine-step__variant > span");
          const variantText = variant ? variant.textContent.trim() : "";
          const v = document.createElement("span");
          v.className = "routine__row-variant";
          v.textContent = variantText !== "Default Title" ? variantText : "";
          // The price under the name, so the name has the row's width (owner, 2026-10-20).
          const price = document.createElement("span");
          price.className = "routine__row-price";
          price.textContent = this.money(Number(el.dataset.price));
          // As on the product page's routine cards: the variant in one line with "Skift" when there is a choice (scent,
          // shade, size), which unfolds the step's own chips under the row; and when in the day it is used (owner,
          // 2026-10-21).
          const groups = guide ? [...el.querySelectorAll(".routine-chips")].filter((g) => g.children.length > 1) : [];
          const changeText = ((el.querySelector("[data-routine-change]") || {}).textContent || "").trim();
          // A shade can't be guessed: its choices start open (the owner left it to us, 2026-10-21), the rest on "Skift".
          const key = el.dataset.stepKey;
          const shade = groups.some((g) => g.hasAttribute("data-shade"));
          const isOpen = groups.length && ((this.openPicks && this.openPicks.has(key)) || (shade && !(this.closedPicks && this.closedPicks.has(key))));
          if (guide) {
            const open = document.createElement("button");
            open.type = "button";
            open.className = "routine__row-open";
            open.textContent = name.textContent;
            name.textContent = "";
            name.append(open);
            if (groups.length && changeText) {
              const change = document.createElement("button");
              change.type = "button";
              change.className = "routine__row-change routine-step__change";
              change.textContent = changeText;
              change.setAttribute("aria-expanded", String(!!isOpen));
              change.addEventListener("click", () => {
                if (!this.openPicks) this.openPicks = new Set();
                if (!this.closedPicks) this.closedPicks = new Set();
                if (isOpen) {
                  this.openPicks.delete(key);
                  this.closedPicks.add(key);
                } else {
                  this.closedPicks.delete(key);
                  this.openPicks.add(key);
                }
                this.renderSummary();
                const again = this.summaryList.querySelector(`[data-step="${CSS.escape(el.dataset.stepKey)}"] .routine__row-change`);
                if (again) again.focus({ preventScroll: true });
              });
              if (!v.textContent) v.textContent = "";
              v.append(" ", change);
            }
          }
          const whenTag = guide && el.querySelector(".routine-step__when");
          const when = whenTag ? whenTag.cloneNode(true) : null;
          if (when) {
            when.classList.add("routine__row-when");
            // Enkel has no night cream: the day cream goes on in the evening too, with SPF too (owner, 2026-10-22: Enkel has no night cream, and the skin wants moisture twice a day; SPF at
            // night does no harm). Not a sunscreen and not a tinted cream.
            const moisturiser = /creme/i.test(el.dataset.type || "") && !/sol/i.test(el.dataset.type || "") && !/solcreme/i.test(name.textContent) && !el.querySelector(".routine-chips[data-shade]");
            const nightCream = this.steps.some((x) => x !== step && !x.el.hidden && !x.el.classList.contains("is-collapsed") && /creme/i.test(x.el.dataset.type || "") && /aften/i.test((x.el.querySelector(".routine-step__when") || {}).textContent || ""));
            const text = when.textContent.trim();
            if (moisturiser && !nightCream && /^morgen$/i.test(text) && this.bothWhen) {
              when.innerHTML = this.bothWhen;
              when.classList.add("routine__row-when");
            }
          }
          text.append(label, name, ...(v.textContent || v.children.length ? [v] : []), ...(when ? [when] : []), price);
          // An extra not added: room for the plus beside the chevron; the plus itself sits on the row (a button can't hold one).
          const end = document.createElement("span");
          end.className = "routine__row-end";
          // A chevron, as list rows that open something do: the row opens the product's details (owner, 2026-10-20:
          // the (i) didn't say "tap the product").
          const chevron = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          chevron.setAttribute("viewBox", "0 0 20 20");
          chevron.setAttribute("aria-hidden", "true");
          chevron.classList.add("routine__row-chevron");
          const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
          use.setAttribute("href", "#icon-chevron");
          chevron.append(use);
          // An extra has the plus as its one icon (as a product card has its bag): the row still opens its details.
          row.append(thumb, text, ...(extra ? [end] : [chevron]));
          (guide ? row.querySelector(".routine__row-open") : row).addEventListener("click", () => {
            // In Find din rutine an add-on not yet in the routine opens the product's details (as "Læs mere"), not the
            // routine's step panel, which had only its folded tip to show (owner, 2026-10-21).
            // In Find din rutine every row opens the product's details in the drawer used everywhere ("Læs mere");
            // scent is the switch above, size is in "Tilpas rutinen" (owner, 2026-10-21).
            const link = this.closest("[data-guide-routine]") && step.el.querySelector("a[href*='/products/']");
            if (link) link.click();
            else this.openSteps(index, true);
          });
          li.append(row);
          // "Skift" unfolded: the step's chips (cloned; a click on one clicks the step's own, so price, picture and stock
          // follow as in "Tilpas rutinen").
          if (isOpen) {
            // One box under the row for all the choices, the labels in one column (owner, 2026-10-21: a line between
            // Duft and Størrelse, the pills not lined up).
            const box = document.createElement("div");
            box.className = "routine__row-pick";
            groups.forEach((group) => {
              const set = document.createElement("div");
              set.className = "routine__row-pick-set";
              set.setAttribute("role", "group");
              set.setAttribute("aria-label", group.getAttribute("aria-label") + ": " + name.textContent);
              const caption = document.createElement("span");
              caption.textContent = group.getAttribute("aria-label") + ":";
              const chips = document.createElement("span");
              chips.className = "routine__row-pick-chips";
              group.querySelectorAll(".routine-chip").forEach((chip) => {
                const copy = chip.cloneNode(true);
                copy.addEventListener("click", () => chip.click());
                chips.append(copy);
              });
              set.append(caption, chips);
              box.append(set);
            });
            li.append(box);
            li.classList.add("has-pick");
          }
          li.dataset.step = el.dataset.stepKey || "";
          // The plus adds the extra to the routine at once; the row still opens its details (owner, 2026-10-21: an icon,
          // not "+ Tilføj").
          if (extra) {
            const add = document.createElement("button");
            add.type = "button";
            add.className = "routine__row-add";
            add.setAttribute("aria-label", addLabel + ": " + name.textContent);
            add.innerHTML = '<span aria-hidden="true"><svg viewBox="0 0 20 20" width="14" height="14"><use href="#icon-plus"></use></svg></span>';
            add.addEventListener("click", () => this.setExpanded(step, true, false));
            li.append(add);
          }
          li.dataset.extra = extra ? "true" : "";
          return li;
        });
      const extras = rows.filter((li) => li.dataset.extra);
      const head = this.querySelector(".routine__steps .routine__extras-head");
      const group = extras.length && head ? [head.cloneNode(true), ...extras] : extras;
      this.summaryList.replaceChildren(...rows.filter((li) => !li.dataset.extra), ...group);
    }

    // From a row: one product's details, so no progress through steps the customer didn't pick ("Tilpas rutinen"
    // keeps the progress).
    openSteps(index, details) {
      this.classList.add("is-stepping");
      this.classList.toggle("is-details", !!details);
      const col = this.querySelector("[data-routine-steps-col]");
      col.setAttribute("role", "dialog");
      col.setAttribute("aria-modal", "true");
      document.documentElement.classList.add("routine-stepping");
      this.stepperOpener = document.activeElement;
      const step = this.sequence()[index];
      this.detailsExtra = details && step && step.el.classList.contains("is-collapsed") ? step : null;
      this.showStep(details && step ? this.stepperList().indexOf(step) : index);
    }

    closeSteps() {
      this.classList.remove("is-stepping", "is-details");
      const col = this.querySelector("[data-routine-steps-col]");
      col.removeAttribute("role");
      col.removeAttribute("aria-modal");
      document.documentElement.classList.remove("routine-stepping");
      this.steps.forEach((s) => s.el.classList.remove("is-step-current"));
      this.renderSummary();
      if (this.stepperOpener && this.stepperOpener.isConnected) this.stepperOpener.focus({ preventScroll: true });
    }

    showStep(index) {
      const list = this.stepperList();
      this.stepIndex = Math.max(0, Math.min(index, list.length - 1));
      list.forEach((s, i) => s.el.classList.toggle("is-step-current", i === this.stepIndex));
      const n = this.stepIndex + 1;
      this.stepperCount.textContent = this.stepperCount.dataset.template.replace("[n]", n).replace("[total]", list.length);
      this.stepperBar.style.width = (n / list.length) * 100 + "%";
      this.prevBtn.disabled = this.stepIndex === 0;
      const last = this.stepIndex === list.length - 1;
      this.nextBtn.setAttribute("aria-label", last ? this.nextBtn.dataset.labelDone : this.nextBtn.dataset.labelNext);
      this.renderThumbs(list);
      const col = this.querySelector("[data-routine-steps-col]");
      col.scrollTop = 0;
      const focusTarget = list[this.stepIndex].el.querySelector(".routine-step__tab, .routine-step__check input");
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    // The head's packshots (details only): the routine's products, the one shown marked (owner, 2026-10-19).
    renderThumbs(list) {
      const box = this.querySelector("[data-routine-stepper-thumbs]");
      if (!box) return;
      if (!this.classList.contains("is-details")) return box.replaceChildren();
      const buttons = list.map((step, i) => {
        if (step.el.classList.contains("is-collapsed")) return null;
        const img = step.el.querySelector(".routine-step__img img");
        const button = document.createElement("button");
        button.type = "button";
        button.className = "routine__stepper-thumb";
        const name = step.el.querySelector(".routine-step__name, .product-card__title, h3");
        button.setAttribute("aria-label", name ? name.textContent.trim() : String(i + 1));
        if (i === this.stepIndex) button.setAttribute("aria-current", "step");
        if (img) {
          const copy = document.createElement("img");
          copy.src = img.currentSrc || img.src;
          copy.alt = "";
          button.append(copy);
        }
        button.addEventListener("click", () => this.showStep(i));
        return button;
      });
      box.replaceChildren(...buttons.filter(Boolean));
    }

    // A step's chips, "Skift", its extra tab and its tick (again after swapTo puts another product in the step).
    bindStep(step) {
      step.el.querySelectorAll(".routine-chip").forEach((chip) => {
        chip.addEventListener("click", () => this.pick(step, chip));
      });
      // "Skift": the options stay folded until asked for.
      const change = step.el.querySelector("[data-routine-change]");
      if (change) {
        change.addEventListener("click", () => {
          const panel = step.el.querySelector(".routine-step__options");
          const open = panel.hidden;
          panel.hidden = !open;
          change.setAttribute("aria-expanded", open ? "true" : "false");
          step.el.classList.toggle("is-changing", open);
        });
      }
      // An extra unfolds from its tab, ticked; unticked, it folds back into the tab.
      const expand = step.el.querySelector("[data-routine-expand]");
      if (expand) expand.addEventListener("click", () => this.setExpanded(step, true));
      step.toggle.addEventListener("change", () => {
        if (expand && !step.toggle.checked) {
          this.setExpanded(step, false);
          // The ticked box is gone: focus what offers the extra now (the chip on desktop, the row on a phone).
          [step.chip, expand].find((el) => el && el.offsetParent !== null)?.focus({ preventScroll: true });
          return;
        }
        step.el.classList.toggle("is-off", !step.toggle.checked);
        this.update();
      });
      this.markChips(step);
    }

    // The guide's answers may prefer another product in a step (Renseskum for dry or mature skin …): its card waits in a
    // <template data-routine-alt> beside the step (sections/productpage-routine.liquid). The swap keeps whether the step
    // was added, and the product it replaces becomes the alternative.
    swapTo(key, handle) {
      const step = this.steps.find((s) => s.el.dataset.stepKey === key);
      if (!step || step.el.dataset.handle === handle) return false;
      const tpl = [...this.querySelectorAll("template[data-routine-alt]")].find((t) => t.dataset.routineAlt === key && t.dataset.handle === handle);
      const fresh = tpl && tpl.content.querySelector("[data-routine-step]");
      if (!fresh) return false;
      const card = fresh.cloneNode(true);
      const wasOpen = !step.el.classList.contains("is-collapsed");
      const back = document.createElement("template");
      back.dataset.routineAlt = key;
      back.dataset.handle = step.el.dataset.handle;
      step.el.replaceWith(card);
      back.content.append(step.el);
      tpl.replaceWith(back);
      step.el = card;
      step.variants = JSON.parse(card.querySelector("[data-routine-variants]").textContent);
      step.toggle = card.querySelector("[data-routine-toggle]");
      this.bindStep(step);
      if (wasOpen && card.classList.contains("is-collapsed")) this.setExpanded(step, true, false);
      this.layout();
      this.update();
      this.markFolds();
      return true;
    }

    disconnectedCallback() {
      document.removeEventListener("variant:changed", this.onVariantChange);
      window.removeEventListener("resize", this.onResize);
      if (this.addObserver) {
        this.addObserver.disconnect();
        document.body.classList.remove("routine-add-in-view");
      }
    }

    // "Læs mere" only where the folded line actually cuts the advice (never on desktop, where it is not folded).
    markFolds() {
      this.querySelectorAll("[data-routine-more]").forEach((btn) => {
        const advice = btn.closest(".routine-step__advice");
        if (advice.classList.contains("is-open")) return;
        const note = advice.querySelector(".routine-step__note");
        btn.hidden = !(note && note.offsetParent !== null && note.scrollHeight > note.clientHeight + 1);
        // A note that fits on its line has nothing to unfold: show it as open, so its product link shows too.
        if (btn.hidden && note && note.offsetParent !== null && window.innerWidth < 980) advice.classList.add("is-open");
      });
    }

    // A new variant in the buy box: the section is fetched again for it. Rapid changes can overlap, so only the
    // newest response is applied, to whichever routine is on the page by then; the shopper's own choices carry over.
    reload(variantId) {
      const section = this.closest(".shopify-section");
      if (!section) return;
      const sectionId = section.id.replace("shopify-section-", "");
      this.dataset.variantId = variantId;
      section.dataset.routineVariant = variantId;
      window.ec
        .sectionFetch(`${location.pathname}?variant=${variantId}`, sectionId)
        .then((html) => {
          if (section.dataset.routineVariant !== String(variantId)) return;
          const fresh = new DOMParser().parseFromString(html, "text/html").querySelector("routine-builder");
          const current = section.querySelector("routine-builder");
          if (!fresh || !current) return;
          // Read the choices now, not when the request left: the shopper may have changed something meanwhile.
          const state = current.choices();
          current.replaceWith(fresh);
          fresh.restore(state);
        })
        .catch(() => {});
    }

    // What the shopper chose by hand, per step: ticked or not, an extra unfolded, a variant picked with the chips.
    choices() {
      return Object.fromEntries(
        this.steps.map((s) => [
          s.el.dataset.stepKey,
          {
            checked: s.toggle.checked,
            expanded: s.el.hasAttribute("data-routine-extra") && !s.el.classList.contains("is-collapsed"),
            variantId: s.el.dataset.touched ? s.el.dataset.variantId : null,
          },
        ])
      );
    }

    // The product in view follows the buy box, so its step keeps the rendered variant; only its tick carries over.
    restore(state) {
      this.steps.forEach((step) => {
        const was = state[step.el.dataset.stepKey];
        if (!was) return;
        if (was.expanded) this.setExpanded(step, true, false);
        if (!step.el.hasAttribute("data-routine-extra") && !was.checked) {
          step.toggle.checked = false;
          step.el.classList.add("is-off");
        }
        if (step.el.classList.contains("routine-step--current")) return;
        const variant = was.variantId && step.variants.find((v) => String(v.id) === was.variantId);
        if (variant) {
          this.setChips(step, variant);
          this.apply(step, variant);
          step.el.dataset.touched = "1";
        }
      });
      this.update();
    }

    setExpanded(step, open, focus = true) {
      step.el.classList.toggle("is-collapsed", !open);
      step.toggle.checked = open;
      step.el.classList.remove("is-off");
      if (step.chip) {
        step.chip.classList.toggle("is-added", open);
        step.chip.setAttribute("aria-pressed", open ? "true" : "false");
      }
      this.layout();
      this.update();
      this.markFolds();
      if (open && focus) step.el.querySelector(".routine-step__check input")?.focus({ preventScroll: true });
    }

    // Desktop columns: one per card in the row; an extra not yet added is offered as a chip in the expert's intro instead.
    layout() {
      const list = this.querySelector(".routine__steps");
      const cards = this.steps.filter((s) => !s.el.classList.contains("is-collapsed")).length;
      // Never wider than a card in a row of four (gap 14px): two or three steps keep that size, centred, instead of
      // stretching to half the section with images taller than the screen.
      // Four or more share the row, but never narrower than 220px: past that the row scrolls sideways (snapping per card)
      // instead of squeezing the step name over the packshot – e.g. seven cards in the guide's panel.
      const cols = cards < 4 ? `minmax(0, calc((100% - 42px) / 4))` : "minmax(220px, 1fr)";
      list.style.setProperty("--routine-cols", `repeat(${cards}, ${cols})`);
      // Centred only while the cards don't fill the row: a centred row wider than its box can't scroll to its start.
      list.style.justifyContent = cards < 4 ? "center" : "start";
      this.renumber();
    }

    // Desktop: each extra as a chip under Lotte's text ("Vil du gå et skridt videre?"), built from its card's pill.
    buildExtraChips(only) {
      const box = this.querySelector("[data-routine-extra-chips]");
      const extras = (only || this.steps).filter((s) => s.el.hasAttribute("data-routine-extra"));
      if (!box || !extras.length) return;
      extras.forEach((step) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "routine__extra-chip";
        btn.setAttribute("aria-pressed", "false");
        const tab = step.el.querySelector("[data-routine-expand]");
        if (tab) btn.setAttribute("aria-label", tab.getAttribute("aria-label"));
        const pill = step.el.querySelector(".routine-step__tab-pill");
        if (pill) btn.append(pill.cloneNode(true));
        btn.addEventListener("click", () => this.setExpanded(step, step.el.classList.contains("is-collapsed")));
        box.append(btn);
        step.chip = btn;
      });
      box.hidden = false;
    }

    // Find din rutine's "Komplet": a fokus step for a further concern (a ready card from <template data-routine-focus>) joins
    // the routine as an extra; the guide unfolds it (guide.js).
    insertStep(card, after) {
      if (after) after.after(card);
      else this.querySelector(".routine__steps .routine__extras-head").before(card);
      const step = { el: card, variants: JSON.parse(card.querySelector("[data-routine-variants]").textContent), toggle: card.querySelector("[data-routine-toggle]") };
      // Kept in page order, so the rows and their numbers follow the order of use.
      this.steps.push(step);
      this.steps.sort((a, b) => (a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
      this.bindStep(step);
      this.buildExtraChips([step]);
      this.layout();
      this.update();
      this.markFolds();
      return step;
    }

    // "Enkel" leaves a basic step out (and a step the customer must avoid stays out): hidden, not ticked, not counted.
    setHidden(step, hidden) {
      step.el.hidden = hidden;
      if (!step.el.hasAttribute("data-routine-extra")) {
        step.toggle.checked = !hidden;
        step.el.classList.toggle("is-off", hidden);
      } else if (hidden && !step.el.classList.contains("is-collapsed")) {
        this.setExpanded(step, false, false);
      }
      if (step.chip) step.chip.hidden = hidden;
      this.layout();
      this.update();
    }

    renumber() {
      const template = this.querySelector(".routine__steps").dataset.stepTemplate;
      let n = 0;
      this.steps.forEach((step) => {
        if (step.el.hidden || step.el.classList.contains("is-collapsed")) return;
        n += 1;
        const number = step.el.querySelector(".routine-step__number");
        if (number) number.textContent = template.replace("[n]", n);
      });
    }

    // The variant the step's chips point at, or null when that combination does not exist.
    selected(step) {
      const values = [...step.el.querySelectorAll(".routine-chips")].map((group) => ({
        index: Number(group.dataset.optionIndex),
        value: group.querySelector(".routine-chip.is-active")?.dataset.value,
      }));
      return step.variants.find((v) => values.every((o) => v.options[o.index] === o.value)) || null;
    }

    pick(step, chip) {
      chip.parentElement.querySelectorAll(".routine-chip").forEach((c) => {
        c.classList.toggle("is-active", c === chip);
        c.setAttribute("aria-pressed", c === chip ? "true" : "false");
      });
      // A combination that does not exist (100 ml only with scent): move the other options to one that does.
      let variant = this.selected(step);
      if (!variant) {
        const index = Number(chip.parentElement.dataset.optionIndex);
        variant = step.variants.find((v) => v.available && v.options[index] === chip.dataset.value) || step.variants.find((v) => v.options[index] === chip.dataset.value);
        if (variant) this.setChips(step, variant);
      }
      if (!variant) return;
      step.el.dataset.touched = "1";
      this.apply(step, variant);
    }

    apply(step, variant) {
      step.el.dataset.variantId = variant.id;
      step.el.dataset.price = variant.price;
      step.el.querySelector("[data-routine-price]").textContent = this.money(variant.price);
      const cap = step.el.querySelector("[data-routine-cap]");
      if (cap) {
        cap.hidden = !(variant.compare > variant.price);
        cap.textContent = cap.hidden ? "" : this.money(variant.compare);
      }
      const summary = step.el.querySelector("[data-routine-summary]");
      if (summary) summary.textContent = variant.options.join(" · ");
      const img = step.el.querySelector(".routine-step__img img");
      if (img && variant.image) {
        img.removeAttribute("srcset");
        img.src = variant.image;
      }
      this.markChips(step);
      this.update();
    }

    setChips(step, variant) {
      step.el.querySelectorAll(".routine-chips").forEach((group) => {
        const value = variant.options[Number(group.dataset.optionIndex)];
        group.querySelectorAll(".routine-chip").forEach((c) => {
          c.classList.toggle("is-active", c.dataset.value === value);
          c.setAttribute("aria-pressed", c.dataset.value === value ? "true" : "false");
        });
      });
    }

    // Sold-out choices are struck through: a chip is out when no variant with it and the other chosen options is in stock.
    markChips(step) {
      const groups = [...step.el.querySelectorAll(".routine-chips")];
      groups.forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        const others = groups
          .filter((g) => g !== group)
          .map((g) => ({ index: Number(g.dataset.optionIndex), value: g.querySelector(".routine-chip.is-active")?.dataset.value }));
        group.querySelectorAll(".routine-chip").forEach((c) => {
          const inStock = step.variants.some(
            (v) => v.available && v.options[index] === c.dataset.value && others.every((o) => v.options[o.index] === o.value)
          );
          c.classList.toggle("is-out", !inStock);
        });
      });
      const variant = this.selected(step);
      step.el.classList.toggle("is-unavailable", !variant || !variant.available);
    }

    ticked() {
      return this.steps.filter((s) => !s.el.hidden && !s.el.classList.contains("is-collapsed") && s.toggle.checked && !s.el.classList.contains("is-unavailable"));
    }

    // The routine's products as checkout counts them (data-routine-products, sections/productpage-routine.liquid: every
    // product of every step, extras and alternatives included, plus the fokus products for a face routine), as
    // .ecooking-tools/routine-discount-sync.mjs writes them into the discount's config.
    listed() {
      if (!this.listedSet) this.listedSet = new Set((this.dataset.routineProducts || "").split("|").filter(Boolean));
      return this.listedSet;
    }

    // Find din rutine: every routine's products (<script data-routine-map>), for a result that holds products of another
    // routine too (a further concern's step). own: the products of its own steps; all: with the fokus products a face
    // routine ("*") also counts at checkout.
    routineMap() {
      if (this.mapCache === undefined) {
        const el = this.querySelector("[data-routine-map]");
        let raw = null;
        try {
          raw = el ? JSON.parse(el.textContent) : null;
        } catch (e) {}
        const split = (v) => v.replace(/^\*/, "").split("|").filter(Boolean);
        const focus = raw ? split(raw["*fokus"] || "") : [];
        this.mapCache = raw
          ? Object.fromEntries(
              Object.entries(raw)
                .filter(([k]) => k !== "*fokus")
                .map(([k, v]) => [k, { own: new Set(split(v)), all: new Set([...split(v), ...(v.startsWith("*") ? focus : [])]) }])
            )
          : null;
      }
      return this.mapCache;
    }

    // The routines the lines name (_routine): this one first, then, for products it doesn't list, the routine with the
    // most of them in its own steps (the fokus products only as a last resort), and so on, sorted. routine-fn counts the
    // products of all named routines together ("haender-og-foedder+fodder"; owner, 2026-10-23: Tørre hænder + Hård hud
    // på fødderne got no discount).
    routinesFor(list) {
      const handles = [this.dataset.routine];
      const map = this.routineMap();
      if (!map) return handles;
      const covered = new Set(this.listed());
      let left = [...new Set(list.map((s) => s.el.dataset.handle))].filter((h) => !covered.has(h));
      const extra = [];
      while (left.length) {
        let best = null;
        let bestScore = 0;
        Object.entries(map).forEach(([handle, r]) => {
          if (handle === this.dataset.routine || extra.includes(handle)) return;
          const score = left.filter((h) => r.own.has(h)).length * 100 + left.filter((h) => r.all.has(h)).length;
          if (score > bestScore) [best, bestScore] = [handle, score];
        });
        if (!best) break;
        extra.push(best);
        map[best].all.forEach((h) => covered.add(h));
        left = left.filter((h) => !covered.has(h));
      }
      return [...handles, ...extra.sort()];
    }

    // The products of the named routines together.
    listedFor(handles) {
      if (handles.length === 1) return this.listed();
      const map = this.routineMap();
      const all = new Set(this.listed());
      handles.slice(1).forEach((h) => map[h] && map[h].all.forEach((x) => all.add(x)));
      return all;
    }

    // The routine discount exactly as checkout gives it (Routine20, free-gift-discount/extensions/routine-fn): lines
    // tagged with the routine(s) whose product is listed in them; it needs max(min(3, products listed), 2) different
    // products; one unit of each is discounted. Owner, 2026-10-23: never show a saving checkout won't give, so every
    // price reduction here (and the Enkel/Komplet prices in guide.js) comes from this.
    dealFor(list) {
      const percent = Number(this.dataset.discount || 0);
      const routines = this.routinesFor(list);
      const listed = this.listedFor(routines);
      const counted = new Set();
      const firsts = list.filter((s) => listed.has(s.el.dataset.handle) && !counted.has(s.el.dataset.handle) && counted.add(s.el.dataset.handle));
      const need = Math.max(2, Math.min(3, listed.size));
      const on = percent > 0 && listed.size > 0 && firsts.length >= need;
      const total = list.reduce((sum, s) => sum + Number(s.el.dataset.price || 0), 0);
      const saving = on ? firsts.reduce((sum, s) => sum + Math.round((Number(s.el.dataset.price || 0) * percent) / 100), 0) : 0;
      return { on, left: need - firsts.length, counted, listed, routines, total, saving, toPay: total - saving };
    }

    update() {
      const ticked = this.ticked();
      const deal = this.dealFor(ticked);
      this.routineTotal = deal.toPay;
      // "I alt: <before struck> <after>" on one row when the discount applies; the plain total otherwise (owner, 2026-10-23).
      this.totalEl.textContent = this.money(deal.toPay);
      const before = this.querySelector("[data-routine-before]");
      if (before) {
        before.hidden = !deal.on;
        before.textContent = deal.on ? this.money(deal.total) : "";
      }
      const hint = this.querySelector("[data-routine-deal]");
      if (hint) {
        // Not reached yet, but the routine's products could reach it: how many more, and the first one the customer can
        // add here with one tap (a step ticked off, an extra on offer, or one the guide's result holds back). Gone the
        // moment the discount applies (owner, 2026-10-23: a result with no saving said nothing).
        const can = (s) => deal.listed.has(s.el.dataset.handle) && !deal.counted.has(s.el.dataset.handle) && !s.el.classList.contains("is-unavailable") && !s.avoided && !ticked.includes(s);
        const offer =
          this.steps.find((s) => can(s) && !s.el.hidden) || this.steps.find((s) => can(s) && s.el.hidden && s.el.hasAttribute("data-routine-extra") && !s.focus);
        const reachable = deal.listed.size - deal.counted.size >= deal.left;
        const show = !deal.on && deal.left > 0 && ticked.length > 0 && reachable && Number(this.dataset.discount || 0) > 0;
        hint.hidden = !show;
        if (!show) hint.replaceChildren();
        else {
          const parts = [deal.left === 1 ? hint.dataset.dealOne : (hint.dataset.dealMany || "").replace("[count]", deal.left)];
          const name = offer && ((offer.el.querySelector(".routine-step__name") || {}).textContent || "").trim();
          if (name && hint.dataset.dealExample) {
            const [before, after = ""] = hint.dataset.dealExample.split("[name]");
            const add = document.createElement("button");
            add.type = "button";
            add.className = "txt-btn-underscore-1 routine__deal-add";
            add.textContent = name;
            add.addEventListener("click", () => this.offerStep(offer));
            parts.push(" " + before, add, after);
          }
          hint.replaceChildren(...parts);
        }
      }
      // Toward free delivery and the gift, from the routine's total after its discount (ec-shared.js).
      const nudge = this.querySelector("[data-routine-nudge]");
      if (nudge && window.ec) window.ec.nudge(ticked.length ? this.routineTotal : 0, nudge, JSON.parse(nudge.dataset.copy || "{}"));
      this.renderSummary && this.renderSummary();
      const count = ticked.length;
      this.addLabel.textContent = count === 1 ? this.addBtn.dataset.labelOne : this.addBtn.dataset.labelMany.replace("[count]", count);
      this.addBtn.disabled = count === 0 || this.adding;
      if (this.adding) this.addLabel.textContent = this.addBtn.dataset.labelBusy;
    }

    // The deal hint's product: into the routine, ticked (an extra held back by the guide's result is shown first).
    offerStep(step) {
      if (step.el.hidden) {
        step.addon = false;
        this.setHidden(step, false);
      }
      if (step.el.classList.contains("is-collapsed")) return this.setExpanded(step, true, false);
      step.toggle.checked = true;
      step.el.classList.remove("is-off");
      this.update();
    }

    // Kept on the section, not the element: a variant change replaces the builder while an add is under way.
    get adding() {
      const section = this.closest(".shopify-section");
      return section ? section.dataset.routineAdding === "1" : Boolean(this._adding);
    }

    set adding(value) {
      const section = this.closest(".shopify-section");
      if (section) section.dataset.routineAdding = value ? "1" : "";
      else this._adding = value;
    }

    add() {
      const ticked = this.ticked();
      const routine = this.routinesFor(ticked).join("+");
      const items = ticked.map((s) => ({
        id: Number(s.el.dataset.variantId),
        quantity: 1,
        // _routine names the routine; the checkout's discount counts its different products (dealFor).
        properties: {
          // "guide" when the Byg din rutine result shows this routine (sections/productpage-routine.liquid, guide mode).
          _atc_source: this.dataset.atcSource || "routine",
          _routine: routine,
          // The guide's answers that led here ("g=ansigt&concern=linjer&age=40"), for the order reports.
          ...(this.dataset.guideAnswers ? { _guide: this.dataset.guideAnswers } : {}),
        },
      }));
      if (!items.length || this.adding) return Promise.resolve({ ok: false });
      const cart = document.querySelector("cart-drawer");
      const section = this.closest(".shopify-section");
      // Whichever builder is on the page when the answer comes back.
      const live = () => (section && section.querySelector("routine-builder")) || this;
      const error = this.querySelector("[data-routine-error]");
      if (error) error.hidden = true;
      this.adding = true;
      this.update();
      // Through the cart's queue, so it never races a quantity change in the drawer.
      const request = cart
        ? cart.postAdd({ items, sections: cart.sectionsToRefresh })
        : fetch(window.Shopify.routes.root + "cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items }),
          }).then((r) => ({ ok: r.ok }));
      return request
        .then((result) => {
          // Added from the step dialog: it closes, so the cart that opens is not under it (the dialog sits above the drawer).
          if (result.ok) new Set([this, live()]).forEach((b) => b.classList.contains("is-stepping") && b.closeSteps());
          // Refused (e.g. a step sold out since the page loaded): say why; the cart already shows what it really holds.
          const message = live().querySelector("[data-routine-error]");
          if (!result.ok && message) {
            message.textContent = result.message || message.dataset.fallback;
            message.hidden = false;
          }
          return result;
        })
        .finally(() => {
          // This builder may have been replaced (and detached) meanwhile: clear the flag on the section itself.
          if (section) section.dataset.routineAdding = "";
          else this.adding = false;
          live().update();
        });
    }

    // Prices are already in the visitor's currency; ec-shared.js prints them as Liquid's `money` does.
    money(cents) {
      return window.ec.money(cents);
    }
  }

  customElements.define("routine-builder", RoutineBuilder);
}
